import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {AppRoutes} from '../../enums/routes';
import {PermissionService} from '../../_auth/permission.service';
import {TabSelectionComponent} from '../x-components/tab-selection/tab-selection.component';
import {TabOptionModel} from '../x-components/tab-selection/models/tab-option-model';
import {DeviceEnrollmentComponent} from '../device-enrollment/device-enrollment.component';
import {DeviceCredentialsComponent} from '../device-credentials/device-credentials.component';

/** The two ways a till is given an identity, as the `:tab` segment of `devices/onboarding/:tab`. */
export type DeviceOnboardingTab = 'qr' | 'credentials';

/** The browser-tab title per onboarding tab; also what the route's `title` resolves to. */
export const DEVICE_ONBOARDING_TITLES: Record<DeviceOnboardingTab, string> = {
  qr: 'Kasse einrichten',
  credentials: 'Zugangsdaten für Kassen'
};

/** Reads the `:tab` segment leniently: anything that is not a known tab is the QR tab. */
export function parseDeviceOnboardingTab(raw: string | number | null | undefined): DeviceOnboardingTab {
  return raw === 'credentials' ? 'credentials' : 'qr';
}

/** The route's `title` resolver — the page title follows the selected tab. */
export function deviceOnboardingTitle(route: ActivatedRouteSnapshot): string {
  return DEVICE_ONBOARDING_TITLES[parseDeviceOnboardingTab(route.paramMap.get('tab'))];
}

/**
 * The register-onboarding page: one shell, two tabs (#469 steps 5 + 7, unified per #279).
 *
 * ### Why one page
 *
 * A QR code and a client id + password are the two ways a till is given an identity, and an administrator
 * should not have to know which of two screens describes the register in front of them. They used to be two
 * routes behind two buttons on *Geräte*; they are now two tabs of one page behind one button. The tab bodies
 * are the former screens unchanged in substance — `DeviceEnrollmentComponent` and `DeviceCredentialsComponent`
 * — with the page chrome (back link, heading, subtitle) lifted up here so it is not drawn twice.
 *
 * ### The tab is the URL
 *
 * `devices/onboarding/:tab` — `qr` or `credentials` — so a tab can be deep-linked and bookmarked, and the old
 * routes (`devices/enrollment`, `devices/credentials`) redirect to their tab. Switching tabs navigates with
 * `replaceUrl`, so the browser's Back leaves the page rather than walking back through tab flips. The
 * component reads the tab off `paramMap` (one route config, so it is not re-created on a switch) and also
 * sets it optimistically on click, which is what lets a story or a test flip tabs without a live router
 * outlet behind it.
 *
 * ### A tab the caller may not open is not shown
 *
 * Each body has its own `PermissionSet` (gated on the same server `[Authorize]`); a tab whose set fails is
 * left out of the strip, and a deep link to it falls back to the first tab that is allowed. The two sets are
 * identical today, so this is a guard against drift, not a live branch.
 */
@Component({
  selector: 'app-device-onboarding',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabSelectionComponent, DeviceEnrollmentComponent, DeviceCredentialsComponent],
  template: `
    <div class="absolute inset-0 overflow-y-auto px-4 py-4 sm:px-6 lg:px-8">
      <div class="mx-auto flex w-full max-w-5xl flex-col gap-4">

        <div class="flex items-center gap-3">
          <a [href]="'/' + overviewRoute" aria-label="Zurück zur Geräteübersicht"
             class="shrink-0 text-gray-500 transition-colors hover:text-gray-900">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
                 xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"/>
            </svg>
          </a>
          <!-- min-w-0 and NO truncate: the subtitle is a sentence, not a device name. It wraps at phone width,
               which is exactly where the explanation matters most. -->
          <div class="min-w-0">
            <h1 class="text-xl font-bold tracking-tight text-gray-900">Kasse einrichten</h1>
            <p class="mt-0.5 text-sm text-gray-500">
              Ein neues Gerät meldet sich mit einem QR-Code oder mit Zugangsdaten an — beides gilt für diesen
              Standort und ohne Konto.
            </p>
          </div>
        </div>

        @if (tabs().length > 1) {
          <app-tab-selection [options]="tabs()" [selectedOption]="selectedOption()"
                             (optionSelected)="select($event)"/>
        }

        @switch (tab()) {
          @case ('credentials') {
            <div role="tabpanel" aria-labelledby="tab-credentials">
              <app-device-credentials/>
            </div>
          }
          @default {
            <div role="tabpanel" aria-labelledby="tab-qr">
              <app-device-enrollment/>
            </div>
          }
        }
      </div>
    </div>
  `
})
export class DeviceOnboardingComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);

  protected readonly overviewRoute = AppRoutes.DeviceOverview;

  /** The tabs this caller may open, in display order. */
  protected readonly tabs = computed<TabOptionModel[]>(() => {
    const all: TabOptionModel[] = [];
    if (this.permissions.CheckPermission(PermissionService.Permissions_DeviceEnrollmentComponent()))
      all.push(new TabOptionModel('qr', 'QR-Code'));
    if (this.permissions.CheckPermission(PermissionService.Permissions_DeviceCredentialsComponent()))
      all.push(new TabOptionModel('credentials', 'Zugangsdaten'));
    return all;
  });

  private readonly requested = signal<DeviceOnboardingTab>('qr');

  /** The tab on screen: the requested one if allowed, else the first allowed one. */
  protected readonly tab = computed<DeviceOnboardingTab>(() => {
    const allowed = this.tabs().map(option => option.id);
    const wanted = this.requested();
    return allowed.includes(wanted) ? wanted : parseDeviceOnboardingTab(allowed[0]);
  });

  protected readonly selectedOption = computed(() => this.tabs().find(option => option.id === this.tab()));

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed())
      .subscribe(params => this.requested.set(parseDeviceOnboardingTab(params.get('tab'))));
  }

  protected select(option: TabOptionModel): void {
    const tab = parseDeviceOnboardingTab(option.id);
    this.requested.set(tab);
    void this.router.navigate(['/' + AppRoutes.DeviceOnboarding, tab], {replaceUrl: true});
  }
}
